import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { activitiesAPI, sitesAPI, creditsAPI, resolveImageUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, Zap, Waves, ArrowLeft, MapPin, Coins, Check, ArrowRight } from 'lucide-react';

const ACTIVITY_ICONS = {
  efoil: Zap,
  esurf: Waves,
  paddle: Waves,
  wingfoil: Waves,
};

export default function ActivitiesPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [site, setSite] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const isIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();

  useEffect(() => {
    activitiesAPI.getAll(siteId).then(r => setActivities(r.data)).catch(() => {});
    sitesAPI.getOne(siteId).then(r => setSite(r.data)).catch(() => {});
    creditsAPI.getPackages(siteId).then(r => setCreditPackages(r.data)).catch(() => {});
  }, [siteId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="activities-page">
      {/* Header */}
      {site && (
        <div className={`relative ${isIframe ? 'h-[200px]' : 'h-[300px]'} overflow-hidden`}>
          <img src={resolveImageUrl(site.image_url)} alt={site.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-10 w-full">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-white/50 hover:text-[#00FF00] text-sm mb-4 transition-colors"
                data-testid="back-to-home"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-[#00FF00]" />
                <span className="text-xs uppercase tracking-widest text-[#00FF00] font-bold">{site.city}</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white" data-testid="site-title">
                {site.name}
              </h1>
              <p className="text-base text-white/50 mt-2 max-w-xl">{site.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Activities Grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
            Activités disponibles
          </h2>
          <p className="text-slate-500 mt-2">{activities.length} activité{activities.length > 1 ? 's' : ''} disponible{activities.length > 1 ? 's' : ''}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {activities.map((activity, index) => {
            const Icon = ACTIVITY_ICONS[activity.type] || Waves;
            const minPrice = Math.min(...activity.durations.map(d => d.price));
            return (
              <div
                key={activity.id}
                className={`activity-card group relative overflow-hidden rounded-2xl bg-[#1A1A1A] shadow-xl border border-[#2A2A2A] flex flex-col md:flex-row animate-fade-in-up stagger-${Math.min(index + 1, 4)}`}
                data-testid={`activity-item-${activity.id}`}
              >
                <div className="relative w-full md:w-64 h-56 md:h-auto flex-shrink-0 overflow-hidden">
                  <img
                    src={resolveImageUrl(activity.image_url)}
                    alt={activity.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col justify-between flex-1">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#00FF00]/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#00FF00]" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white">{activity.name}</h3>
                    </div>
                    <p className="text-sm text-slate-400 mb-4">{activity.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {activity.durations.map((dur) => (
                        <Badge key={dur.duration_minutes} variant="secondary" className="bg-[#2A2A2A] text-slate-300 border-0">
                          <Clock className="w-3 h-3 mr-1" />
                          {dur.label} - {dur.price}€
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-heading text-lg font-bold text-[#00FF00]">Dès {minPrice}€</span>
                    <Button
                      onClick={() => navigate(`/booking/${activity.id}`)}
                      className="btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_20px_rgba(0,255,0,0.2)] font-bold"
                      data-testid={`book-btn-${activity.id}`}
                    >
                      Réserver
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Credit Packages Section */}
      {creditPackages.length > 0 && (
        <section className="pb-20 bg-[#0A0A0A]" data-testid="credit-packages-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="border-t border-[#1A1A1A] pt-16">
              {/* Section Header */}
              <div className="text-center mb-14">
                <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/30 mb-6 font-accent text-xs uppercase tracking-widest">
                  Économisez plus
                </Badge>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                  Forfaits <span className="text-[#00FF00] text-glow">Crédits</span>
                </h2>
                <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
                  Achetez des crédits à l'avance et bénéficiez de bonus exclusifs. Utilisez-les pour réserver vos sessions en un clic.
                </p>
              </div>

              {/* How it works */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
                {[
                  { step: '1', title: 'Achetez un forfait', desc: 'Choisissez le forfait adapté à votre pratique et recevez des crédits bonus.' },
                  { step: '2', title: 'Réservez vos sessions', desc: '1 crédit = 1€. Payez vos réservations directement avec votre solde crédits.' },
                  { step: '3', title: 'Profitez sans limites', desc: 'Vos crédits sont valables sur ce site. Rechargez quand vous voulez.' },
                ].map((item) => (
                  <div key={item.step} className="relative bg-[#111111] rounded-2xl border border-[#1A1A1A] p-6 hover:border-[#00FF00]/30 transition-all duration-300">
                    <div className="w-10 h-10 rounded-full bg-[#00FF00]/15 flex items-center justify-center mb-4">
                      <span className="font-heading text-lg font-bold text-[#00FF00]">{item.step}</span>
                    </div>
                    <h3 className="font-heading text-base font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Packages */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {creditPackages.map(pkg => {
                  const bonus = pkg.credits - pkg.price;
                  const bonusPct = ((pkg.credits / pkg.price - 1) * 100).toFixed(0);
                  return (
                    <div key={pkg.id}
                      className="relative overflow-hidden rounded-2xl bg-[#111111] border border-[#1A1A1A] p-6 hover:border-[#00FF00]/40 transition-all duration-300 group"
                      data-testid={`site-package-${pkg.id}`}>
                      {bonus > 0 && (
                        <div className="absolute top-4 right-4">
                          <Badge className="bg-orange-500/15 text-orange-400 border-0 text-xs font-bold">
                            +{bonusPct}% BONUS
                          </Badge>
                        </div>
                      )}
                      <Coins className="w-8 h-8 text-[#00FF00]/40 mb-4 group-hover:text-[#00FF00]/70 transition-colors" />
                      <h4 className="font-heading text-lg font-bold text-white mb-1">{pkg.name}</h4>
                      {pkg.description && <p className="text-xs text-slate-500 mb-4">{pkg.description}</p>}
                      <div className="mb-4">
                        <span className="font-heading text-3xl font-bold text-[#00FF00]">{pkg.credits}€</span>
                        <span className="text-sm text-slate-500 ml-1">de crédits</span>
                      </div>
                      <p className="text-sm text-slate-400 mb-2">
                        pour <span className="font-bold text-white">{pkg.price}€</span>
                      </p>
                      {bonus > 0 && (
                        <p className="text-xs text-[#00FF00] flex items-center gap-1 mb-5">
                          <Check className="w-3 h-3" /> Vous économisez {bonus}€
                        </p>
                      )}
                      <Button
                        onClick={() => navigate('/credits')}
                        className="w-full btn-pill bg-[#00FF00]/10 text-[#00FF00] border border-[#00FF00]/30 hover:bg-[#00FF00] hover:text-black font-bold transition-all duration-300"
                        data-testid={`buy-package-${pkg.id}`}
                      >
                        Acheter ce forfait
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
